import { IconToggleLeft, IconToggleRight, IconTrash } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getApi } from '../../../api/call.api';
import { urlApi } from '../../../api/url.api';
import IconButton from '../../../components/Buttons/IconButton';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import TableComponent from '../../../components/DataTable/TableComponent';
import Pagination from '../../../components/Pagination/Pagination';
import { loadingHide, loadingShow } from '../../../loadingUi/loading.controller';
import { dateTimeConverter } from '../../../utils/dateTimeConverter';
import AddTeamMemberModal from './AddTeamMemberModal';
import AssignAccessModal from './AssignAccessModal';
import { accessCheck } from '../../../utils/accessCheck';
import { accessKeys } from '../../../statics/accessKeys';
import { checkDomain } from '../../../utils/domainCheck.util';

const TeamList = () => {
    const columns = [
        {
            name: "Role", selector: (row) => row?.channel_name, grow: 1, cell: (row) => (
                <p className="mb-0.5 font-semibold text-sm text-primary">{row?.role == 1 ? "Admin" : "Member"}</p>
            )
        }, {
            name: "Email", center: "true", selector: (row) => row?.title, cell: (row) => (
                <p className="mb-0">{row?.email ?? "--"}</p>
            )
        }, {
            name: "Name", center: "true", selector: (row) => row?.category, cell: (row) => (
                <p className="mb-0 font-medium">{row?.name}</p>
            )
        }, {
            name: "Added", center: "true", selector: (row) => row?.category, cell: (row) => (
                <div>
                    <p className="mb-0">{row?.invitedBy}</p>
                    <p className="mb-0">{dateTimeConverter(row?.createdAt)}</p>
                </div>
            )
        }, {
            name: "Status", center: "true", selector: (row) => row?.status, cell: (row) => (
                <div className='my-1 flex flex-wrap !gap-3'>
                    {accessCheck(accessKeys?.updateTeamMemberStatus) ? <ConfirmationModal buttonComponent={<IconButton icon={row?.status == 1 ? <IconToggleRight color='green' /> : <IconToggleLeft color='red' />} />} confirmationText={`Are You sure want to ${row?.status == 1 ? 'Inactive' : 'Active'} this ${row?.name} ?`} onConfirm={() => updateStatus(row?.userId, row?.status == 1 ? 2 : 1)} /> : (row?.status == 1 ? <IconToggleRight color='green' /> : <IconToggleLeft color='red' />)}
                </div>
            )
        }, {
            name: "Live Chat Access", center: "true", selector: (row) => row?.liveChatAccess, cell: (row) => (
                <div className='my-1 flex flex-wrap !gap-3'>
                    {accessCheck(accessKeys?.assignChatPreset) ? <ConfirmationModal buttonComponent={<IconButton icon={row?.liveChatAccess == 1 ? <IconToggleRight color='green' /> : <IconToggleLeft color='red' />} />} confirmationText={`Are You sure want to ${row?.liveChatAccess == 1 ? 'Revoke' : 'Grant'} Live Chat Access of ${row?.name} ?`} onConfirm={() => assignLiveChatAccess(row?.userId, row?.liveChatAccess == 1 ? 2 : 1)} /> : (row?.liveChatAccess == 1 ? <IconToggleRight color='green' /> : <IconToggleLeft color='red' />)}
                </div>
            )
        }, {
            name: "Action", center: "true", omit: (!accessCheck(accessKeys?.updateTeamMemberStatus) && (!accessCheck(accessKeys?.assignAccessPreset) || !accessCheck(accessKeys?.viewAccessPresets))), selector: (row) => row?.category, cell: (row) => (
                <div className='my-1 flex flex-wrap !gap-3'>
                    {(accessCheck(accessKeys?.assignAccessPreset) && accessCheck(accessKeys?.viewAccessPresets) && checkDomain() === "app") && <AssignAccessModal memberData={row} callback={() => getTableData()} />}
                    {accessCheck(accessKeys?.updateTeamMemberStatus) && <ConfirmationModal buttonComponent={<IconButton icon={<IconTrash size={16} />} />} confirmationText={"Are You sure want to remove this user from your organization?"} onConfirm={() => updateStatus(row?.userId, 3)} />}

                </div>
            )
        },
    ];

    const [tableData, setTableData] = useState({
        data: [],
        totalCount: 0
    });

    const [activePage, setActivePage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    async function getTableData() {
        loadingShow();
        let resp = await getApi(urlApi.getTeamMemberList);
        loadingHide();
        if (resp.responseCode === 200) {
            setTableData({
                data: resp.data.items,
                totalCount: resp.data.count
            });
        } else {
            setTableData({
                data: [],
                totalCount: 0
            });
        }
    }

    function handlePageChange(page) {
        setActivePage(page);
    }

    function handlePerRowsChange(newPerPage, page) {
        setActivePage(page);
        setPerPage(newPerPage)
    }

    useEffect(() => {
        getTableData();
    }, [activePage]);

    const updateStatus = async (userId, status) => {
        loadingShow();
        let resp = await getApi(urlApi.updateTeamMemberStatus + userId + "/" + status);
        loadingHide();
        if (resp.responseCode === 200) {
            toast.success(resp.message);
            getTableData();
        } else {
            toast.error(resp.message);
        }
    }

    const assignLiveChatAccess = async (userId, status) => {
        loadingShow();
        let resp = await getApi(urlApi.assignLiveChatAccess + userId + "/" + status);
        loadingHide();
        if (resp.responseCode === 200) {
            toast.success(resp.message);
            getTableData();
        } else {
            toast.error(resp.message);
        }
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <p className='text-[16px] font-semibold mb-0'>My Team</p>
                {accessCheck(accessKeys?.inviteMemberToOrganization) && <AddTeamMemberModal callback={() => getTableData()} buttonComponent={<button className='primary-btn'>Invite Member</button>} />}
            </div>
            {accessCheck(accessKeys?.viewTeamMembers) && <>
                <TableComponent
                    isHeader={true}
                    columns={columns}
                    data={tableData.data}
                    totalRows={tableData.totalCount}
                    isPagination={false}
                    paginationPerPage={perPage}
                    handlePageChange={(e) => handlePageChange(e)}
                    handlePerRowsChange={(newPerPage, page) => handlePerRowsChange(newPerPage, page)} />

                <Pagination itemsPerPage={perPage} totalCount={tableData.totalCount} pageChangeFunction={(pageNum) => handlePageChange(pageNum)} currentPage={activePage} />
            </>}
        </div>
    )
}

export default TeamList